import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Add, Edit } from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import _ from "lodash";
import Layout from "../components/Layout";
import AudioVoicesManager from "../components/AudioVoicesManager";
import {
  getCourseById,
  updateCourseById,
  uploadFlag,
} from "../service/service";

const EditCourse = () => {
  const { courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [originalUnits, setOriginalUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const tab = searchParams.get("tab") || "details";

  const fetchCourse = useCallback(async () => {
    try {
      const result = await getCourseById(courseId);
      setCourse(result.data);
      setOriginalUnits(result.data.units || []);
    } catch {
      setToast({
        open: true,
        severity: "error",
        message: "Could not load this course.",
      });
    } finally {
      setLoading(false);
    }
  }, [courseId]);
  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  const updateCourse = async () => {
    const units = _.differenceWith(course.units, originalUnits, _.isEqual).map(
      (unit) => ({
        _id: unit._id,
        isBonus: unit.isBonus,
        name: unit.name.trim(),
        description: unit.description.trim(),
      }),
    );
    try {
      const result = await updateCourseById(courseId, {
        name: course.name.trim(),
        description: course.description.trim(),
        units,
      });
      await fetchCourse();
      setToast({
        open: true,
        severity: "success",
        message: result.message || "Course saved.",
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error.response?.data?.err || "Could not save the course.",
      });
    }
  };
  const uploadCourseImage = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("courseId", courseId);
    try {
      await uploadFlag(formData);
      setSelectedFile(null);
      await fetchCourse();
      setToast({
        open: true,
        severity: "success",
        message: "Course image uploaded.",
      });
    } catch {
      setToast({
        open: true,
        severity: "error",
        message: "Course image upload failed.",
      });
    }
  };
  if (loading)
    return (
      <Backdrop open sx={{ zIndex: 1 }}>
        <CircularProgress />
      </Backdrop>
    );
  if (!course)
    return (
      <Layout>
        <Alert severity="error">Course not found.</Alert>
      </Layout>
    );
  const setTab = (_, value) =>
    setSearchParams(value === "details" ? {} : { tab: value });
  const updateUnit = (id, patch) =>
    setCourse({
      ...course,
      units: course.units.map((unit) =>
        unit._id === id ? { ...unit, ...patch } : unit,
      ),
    });

  return (
    <Layout>
      <Box sx={{ p: 2, maxWidth: 1280, mx: "auto" }}>
        <Typography variant="h4">{course.name}</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Course settings, curriculum, and audio operations.
        </Typography>
        <Tabs value={tab} onChange={setTab} sx={{ mb: 3 }}>
          <Tab value="details" label="Details" />
          <Tab value="curriculum" label="Curriculum" />
          <Tab value="audio" label="Audio & Voices" />
        </Tabs>
        {tab === "details" && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
              {course.image && (
                <Box
                  component="img"
                  src={course.image}
                  alt="Course"
                  sx={{
                    width: 190,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 1,
                  }}
                />
              )}
              <Button component="label" variant="outlined">
                Choose course image
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    setSelectedFile(event.target.files?.[0] || null)
                  }
                />
              </Button>
              {selectedFile && (
                <Button onClick={uploadCourseImage}>Upload image</Button>
              )}
            </Box>
            <TextField
              label="Course name"
              fullWidth
              value={course.name}
              onChange={(event) =>
                setCourse({ ...course, name: event.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={4}
              value={course.description}
              onChange={(event) =>
                setCourse({ ...course, description: event.target.value })
              }
            />
            <Button variant="contained" sx={{ mt: 2 }} onClick={updateCourse}>
              Save course
            </Button>
          </Paper>
        )}
        {tab === "curriculum" && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h6">Units</Typography>
              <Button
                component={Link}
                to={`/course/${courseId}/add-unit`}
                variant="contained"
                endIcon={<Add />}
              >
                Add unit
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Bonus</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {course.units.map((unit) => (
                    <TableRow key={unit._id}>
                      <TableCell>{unit.order}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={unit.name}
                          onChange={(event) =>
                            updateUnit(unit._id, { name: event.target.value })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={unit.description}
                          onChange={(event) =>
                            updateUnit(unit._id, {
                              description: event.target.value,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={unit.isBonus}
                              onChange={(event) =>
                                updateUnit(unit._id, {
                                  isBonus: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Bonus"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          component={Link}
                          to={`/course/${courseId}/units/${unit._id}/edit`}
                          endIcon={<Edit />}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button variant="contained" sx={{ mt: 2 }} onClick={updateCourse}>
              Save curriculum
            </Button>
          </Paper>
        )}
        {tab === "audio" && (
          <AudioVoicesManager course={course} onCourseChange={setCourse} />
        )}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
        >
          <Alert severity={toast.severity}>{toast.message}</Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};
export default EditCourse;
